"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useRef, useEffect } from "react";
import Script from "next/script";
import VideoPlayer from "../components/VideoPlayer";

// Import the custom hook
import useIntersectionObserver from "@/components/hooks/useIntersectionObserver";

// To avoid hydration issues, wrap the entire component content in a useEffect
// to ensure consistency between server and client rendering
export default function IMotionSystem() {
  // State to control button visibility to fix navigation issues - default to true
  // to avoid hydration mismatch, then control with useEffect
  const [isClient, setIsClient] = useState(false);

  // Ensure we only use client-side features after mounting
  useEffect(() => {
    // Mark that we're on the client
    setIsClient(true);
  }, []);

  return (
    <>
      {/* Product structured data for better search results */}
      <Script id="i-motion-structured-data" type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "Product",
          "name": "I-motion EMS System",
          "image": [
            "https://emspulse.co.uk/images/i-motion EMS Suit back front.png"
          ],
          "description": "Advanced wireless EMS suit system engineered for athletic performance enhancement, strength development, and recovery optimization.",
          "brand": {
            "@type": "Brand",
            "name": "I-motion"
          },
          "offers": {
            "@type": "Offer",
            "url": "https://emspulse.co.uk/i-motion-system/",
            "priceCurrency": "GBP",
            "priceValidUntil": "2024-12-31",
            "availability": "https://schema.org/InStock",
            "seller": {
              "@type": "Organization",
              "name": "EMS Pulse"
            }
          }
        })}
      </Script>
      <div className="min-h-screen">
        {/* Hero Section with Model Image */}
        <section className="relative pt-20 sm:pt-12 pb-10 sm:pb-16 px-4 bg-gradient-to-br from-[#1a2e4c] via-[#0a4075] to-[#083766] text-white overflow-hidden -mt-[250px]">
          {/* Abstract shapes */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-40 right-1/4 w-64 sm:w-96 h-64 sm:h-96 rounded-full bg-[#2389db] blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 left-1/5 w-60 sm:w-80 h-60 sm:h-80 rounded-full bg-[#38b5a0] blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
            <div className="absolute top-60 left-1/4 w-32 sm:w-40 h-32 sm:h-40 rounded-full bg-[#2389db]/50 blur-xl animate-pulse" style={{animationDelay: '1.5s'}}></div>
          </div>
          
          <div className="max-w-7xl mx-auto relative z-10 mt-40 sm:mt-44 md:mt-52">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
              {/* Text content */}
              <div className="text-center md:text-left order-2 md:order-1 md:pt-12">
                <span className="inline-block bg-[#38b5a0]/20 text-white text-sm sm:text-base mb-2 sm:mb-3 py-1 px-3 sm:px-4 rounded-full border border-[#38b5a0]/30 animate-[fadeSlideUp_0.7s_0.2s_forwards] opacity-0">
                  Performance Focus
                </span>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 leading-tight animate-[fadeSlideUp_0.7s_0.4s_forwards] opacity-0">
                  I-motion EMS System | <span className="text-[#2389db]">Professional Performance</span> Technology
                </h1>
                <p className="text-base sm:text-lg opacity-0 max-w-xl mb-5 sm:mb-6 animate-[fadeSlideUp_0.7s_0.6s_forwards]">
                  The pinnacle of wireless EMS technology for performance-focused businesses.
                </p>
                
                {/* Mobile-only button - server-safe implementation */}
                <div className="block md:hidden w-full mb-4">
                  <Link
                    href="/contact"
                    className="block w-full py-3 px-6 rounded-lg text-center text-white font-medium"
                    style={{
                      backgroundColor: '#2389DB'
                    }}
                  >
                    <span className="flex items-center justify-center">
                      Request Demo 
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 ml-2">
                        <path fillRule="evenodd" d="M2 10a.75.75 0 01.75-.75h12.59l-2.1-1.95a.75.75 0 111.02-1.1l3.5 3.25a.75.75 0 010 1.1l-3.5 3.25a.75.75 0 11-1.02-1.1l2.1-1.95H2.75A.75.75 0 012 10z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </Link>
                </div>
                
                {/* Desktop button container - separate from mobile version */}
                <div className="hidden md:flex md:justify-start animate-[fadeSlideUp_0.7s_0.8s_forwards] opacity-0">
                  <Link
                    href="/contact"
                    className="w-auto bg-[#2389DB] text-white font-medium px-8 py-3 rounded-lg hover:bg-[#1976C5] transition-all shadow-lg text-center group"
                  >
                    <span className="inline-flex items-center">
                      Request Demo 
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform">
                        <path fillRule="evenodd" d="M2 10a.75.75 0 01.75-.75h12.59l-2.1-1.95a.75.75 0 111.02-1.1l3.5 3.25a.75.75 0 010 1.1l-3.5 3.25a.75.75 0 11-1.02-1.1l2.1-1.95H2.75A.75.75 0 012 10z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </Link>
                </div>
              </div>
              
              {/* Image container - adjusted for mobile */}
              <div className="relative h-[280px] sm:h-[320px] md:h-[450px] lg:h-[520px] flex justify-center items-end order-1 md:order-2 mb-4 md:mb-0 z-20 md:pr-4">
                <div className="absolute inset-0 bg-gradient-to-r from-[#2389db]/30 to-transparent rounded-2xl -rotate-3 transform scale-95 opacity-30 blur-sm"></div>
                <div className="relative h-full w-full flex items-end">
                  <Image
                    src="/images/ems-suit-muscle.png"
                    alt="Professional athlete wearing the I-motion EMS system"
                    width={500}
                    height={700}
                    className="object-contain h-full w-auto mx-auto"
                    priority={true}
                    loading="eager"
                    style={{ marginBottom: '-10px' }}
                    fetchPriority="high"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Wave divider - removed margin to make image flush */}
          <div className="absolute bottom-0 left-0 w-full overflow-hidden z-10">
            <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-[40px] sm:h-[60px]">
              <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-white"></path>
            </svg>
          </div>
        </section>

        {/* Technology Section */}
        <section className="py-16 sm:py-20 px-4 bg-gradient-to-br from-[#F8F9FA] to-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#2389db]/10 text-[#2389db] font-medium px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm mb-3 sm:mb-4">
                CUTTING-EDGE TECHNOLOGY
              </span>
              <h2 className="text-xl sm:text-2xl md:text-4xl font-bold mb-2 sm:mb-3 text-[#212529]">
                Professional EMS Technology
              </h2>
              <p className="text-sm sm:text-base text-[#212529] max-w-2xl mx-auto">
                Experience the power of professional EMS training with I-motion's advanced technology
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 sm:gap-10">
              <div className="bg-white rounded-xl border border-[#DEE2E6] shadow-lg p-8 transition-all hover:translate-y-[-5px] hover:shadow-xl duration-300">
                <div className="flex flex-col">
                  <div className="bg-gradient-to-br from-[#2389db] to-[#165588] p-3 rounded-xl text-white inline-block mb-5 self-start shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-[#212529]">Advanced Muscle Activation</h3>
                  <p className="text-[#212529] mb-6">
                    I-motion's professional EMS technology delivers precise, controlled electrical impulses that 
                    activate up to 90% of muscle fibers simultaneously, far exceeding traditional training methods.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="bg-imotion-100 p-1 rounded-md mt-0.5">
                        <svg className="w-5 h-5 text-imotion" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-[#212529]">Simultaneous activation of multiple muscle groups</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="bg-imotion-100 p-1 rounded-md mt-0.5">
                        <svg className="w-5 h-5 text-imotion" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-[#212529]">Precise control over intensity and frequency</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="bg-imotion-100 p-1 rounded-md mt-0.5">
                        <svg className="w-5 h-5 text-imotion" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-[#212529]">Customizable programs for different goals</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="relative bg-gradient-to-br from-[#F8F9FA] to-[#DEE2E6] rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#2389db]/10 rounded-full blur-2xl"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#2389db]/10 rounded-full blur-2xl"></div>
                <div className="aspect-w-16 aspect-h-9 relative z-10 p-6">
                  <Image
                    src="/images/i-motion EMS Suit back front.png"
                    alt="I-motion EMS Technology"
                    width={600}
                    height={400}
                    className="object-contain w-full h-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Model Showcase Section */}
        <section className="py-16 sm:py-20 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#2389db]/10 text-[#2389db] font-medium px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm mb-3 sm:mb-4">
                PROFESSIONAL SYSTEMS
              </span>
              <h2 className="text-xl sm:text-2xl md:text-4xl font-bold mb-2 sm:mb-3 text-slate-900">
                I-motion System Models
              </h2>
              <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
                Choose the perfect EMS system for your business needs
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 md:gap-10">
              {/* Adjusted for better mobile view */}
              <div className="relative bg-gradient-to-br from-[#1f7ac3]/10 to-[#2389db]/5 rounded-2xl overflow-hidden shadow-lg flex items-end justify-center p-6 mb-6 md:mb-0 hover:shadow-xl transition-all min-h-[350px]">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#2389db]/10 rounded-full blur-2xl"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#2389db]/10 rounded-full blur-2xl"></div>
                <Image
                  src="/images/Technology-in-EMS-suit-wireless-ems-training.png"
                  alt="I-motion System Models"
                  width={350}
                  height={400}
                  className="h-[350px] md:h-[400px] w-auto object-contain relative z-10"
                  style={{ marginBottom: '-25px' }}
                />
              </div>
              
              <div className="bg-white rounded-xl border border-slate-100 shadow-lg p-6 sm:p-8 transition-all hover:translate-y-[-5px] hover:shadow-xl duration-300 flex flex-col justify-center">
                <div className="bg-gradient-to-br from-[#38b5a0] to-[#2a8f7e] p-3 rounded-xl text-white inline-block mb-5 self-start shadow-md">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09ZM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4 text-slate-900">Professional-Grade Systems</h3>
                <p className="text-slate-700 mb-6">
                  Our I-motion systems are designed specifically for professional use, with advanced features 
                  and robust construction to withstand the demands of commercial training environments.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <div className="bg-primary-100 p-1 rounded-md mt-0.5">
                      <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-slate-700">Multiple channel configurations available</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="bg-primary-100 p-1 rounded-md mt-0.5">
                      <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-slate-700">Wireless operation for maximum freedom</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="bg-primary-100 p-1 rounded-md mt-0.5">
                      <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-slate-700">Durable construction for commercial use</span>
                  </li>
                </ul>
                <div className="mt-auto">
                  <Link
                    href="/contact"
                    className="inline-flex items-center justify-center w-full sm:w-auto bg-imotion text-white font-medium px-6 py-3 rounded-lg hover:bg-imotion-hover transition-all space-x-2 shadow-md group"
                  >
                    <span>Request System Information</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 sm:py-20 px-4 bg-gradient-to-br from-slate-50 to-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] font-medium px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm mb-3 sm:mb-4">
                KEY BENEFITS
              </span>
              <h2 className="text-xl sm:text-2xl md:text-4xl font-bold mb-2 sm:mb-3 text-slate-900">
                Why Choose I-motion
              </h2>
              <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
                Discover the advantages of professional EMS training
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
              <div className="bg-white rounded-xl border border-slate-100 shadow-lg p-8 transition-all hover:translate-y-[-5px] hover:shadow-xl duration-300">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-imotion p-3 rounded-xl text-white mb-5 flex-shrink-0 shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-slate-900">Time Efficiency</h3>
                  <p className="text-slate-700">
                    Achieve the same results as traditional training in a fraction of the time, 
                    making it perfect for busy professionals and athletes.
                  </p>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-slate-100 shadow-lg p-8 transition-all hover:translate-y-[-5px] hover:shadow-xl duration-300">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-secondary p-3 rounded-xl text-white mb-5 flex-shrink-0 shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-slate-900">Enhanced Results</h3>
                  <p className="text-slate-700">
                    Experience superior muscle activation and faster progress compared to 
                    traditional training methods.
                  </p>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-slate-100 shadow-lg p-8 transition-all hover:translate-y-[-5px] hover:shadow-xl duration-300">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-imodel p-3 rounded-xl text-white mb-5 flex-shrink-0 shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-slate-900">Professional Support</h3>
                  <p className="text-slate-700">
                    Benefit from comprehensive training, ongoing support, and a network of 
                    professional EMS practitioners.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ideal For Section with Image Placeholders */}
        <section className="py-16 sm:py-20 px-4 bg-white relative overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 right-0 w-full h-full bg-[url('/pattern.svg')] bg-no-repeat bg-cover"></div>
          </div>
          
          <div className="max-w-5xl mx-auto relative z-10">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] font-medium px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm mb-3 sm:mb-4">
                PERFECT SOLUTION FOR
              </span>
              <h2 className="text-xl sm:text-2xl md:text-4xl font-bold mb-2 sm:mb-3 text-slate-900">
                Ideal For:
              </h2>
              <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
                Discover how the I-motion system can elevate these business types
              </p>
            </div>
            
            {/* Grid with Image Placeholders in Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Card 1 */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col text-center shadow-md hover:shadow-lg transition-shadow">
                <div className="bg-slate-100 aspect-video w-full flex items-center justify-center text-slate-500 font-medium text-xs sm:text-sm border-b border-slate-200">
                  <Image 
                    src="/images/placeholders/ems-training-gym.jpg"
                    alt="Performance Gyms & Training Facilities"
                    width={400}
                    height={225}
                    className="w-full h-auto object-cover"
                    priority
                  />
                </div>
                <div className="p-4 sm:p-5 flex flex-col items-center flex-grow">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#38b5a0]/10 rounded-full flex items-center justify-center mb-3 sm:mb-4 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-[#38b5a0]">
                      {/* Dumbbell Icon */}
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0 1 12 21 8.25 8.25 0 0 1 6.038 7.047A8.287 8.287 0 0 0 9 9.601a8.983 8.983 0 0 1 3.361-6.867a8.21 8.21 0 0 0 3 2.48Z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 0 0 .495-7.468 5.999 5.999 0 0 0-1.925 3.547a5.975 5.975 0 0 1-2.133-1.001A3.75 3.75 0 0 0 12 18Z" />
                    </svg>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-slate-900">Performance Gyms & Training Facilities</h3>
                  <p className="text-xs sm:text-sm text-slate-700">Enhance your service offerings with elite EMS technology for performance clients</p>
                </div>
              </div>
              
              {/* Card 2 */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col text-center shadow-md hover:shadow-lg transition-shadow">
                <div className="bg-slate-100 aspect-video w-full flex items-center justify-center text-slate-500 font-medium text-xs sm:text-sm border-b border-slate-200">
                  <Image 
                    src="/images/placeholders/ems-suit-sport-center.jpg"
                    alt="Sports Conditioning Centers"
                    width={400}
                    height={225}
                    className="w-full h-auto object-cover"
                    priority
                  />
                </div>
                <div className="p-4 sm:p-5 flex flex-col items-center flex-grow">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#2389db]/10 rounded-full flex items-center justify-center mb-3 sm:mb-4 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-[#2389db]">
                      {/* Star Icon */}
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456Z" />
                    </svg>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-slate-900">Sports Conditioning Centers</h3>
                  <p className="text-xs sm:text-sm text-slate-700">Integrate advanced EMS technology to enhance athletic performance and recovery protocols</p>
                </div>
              </div>
              
              {/* Card 3 */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col text-center shadow-md hover:shadow-lg transition-shadow">
                <div className="bg-slate-100 aspect-video w-full flex items-center justify-center text-slate-500 font-medium text-xs sm:text-sm border-b border-slate-200">
                  <Image 
                    src="/images/placeholders/ems-training-elite.jpg"
                    alt="Elite Personal Trainers"
                    width={400}
                    height={225}
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div className="p-4 sm:p-5 flex flex-col items-center flex-grow">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#f8c156]/10 rounded-full flex items-center justify-center mb-3 sm:mb-4 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-[#f8c156]">
                      {/* User Icon */}
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                    </svg>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-slate-900">Elite Personal Trainers</h3>
                  <p className="text-xs sm:text-sm text-slate-700">Differentiate your services with premium EMS training to attract high-value clients</p>
                </div>
              </div>
              
              {/* Card 4 */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col text-center shadow-md hover:shadow-lg transition-shadow">
                <div className="bg-slate-100 aspect-video w-full flex items-center justify-center text-slate-500 font-medium text-xs sm:text-sm border-b border-slate-200">
                  <Image 
                    src="/images/placeholders/ideal-for-4.jpg"
                    alt="Rehab & Performance Therapy Clinics"
                    width={400}
                    height={225}
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div className="p-4 sm:p-5 flex flex-col items-center flex-grow">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#e05c88]/10 rounded-full flex items-center justify-center mb-3 sm:mb-4 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-[#e05c88]">
                      {/* Shield Check Icon */}
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
                    </svg>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-slate-900">Rehab & Performance Therapy Clinics</h3>
                  <p className="text-xs sm:text-sm text-slate-700">Support rehabilitation and recovery with controlled muscle activation technology</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Professional Athletes Gallery Section - New Section */}
        <section className="py-16 sm:py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <span className="inline-block bg-[#3bb5ff]/10 text-[#3bb5ff] font-medium px-4 py-1 rounded-full text-sm mb-4">
                TRUSTED BY PROFESSIONALS
              </span>
              <h2 className="text-2xl md:text-4xl font-bold mb-2 sm:mb-4 text-slate-900">
                Elite Athletes <span className="text-[#2389db]">Trust</span> I-motion Technology
              </h2>
              <p className="text-slate-600 max-w-3xl mx-auto">
                See how professional athletes across different sports use I-motion EMS technology to enhance their training and performance
              </p>
            </div>
            
            {/* Athletes Gallery with Video - Based on CSS-Tricks and David Putney techniques */}
            <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
              {/* Video Column */}
              <div className="lg:col-span-2 flex items-center justify-center">
                <ImagePlaceholder />
              </div>
              
              {/* Images Masonry - Using object-fit approach from CSS-Tricks */}
              <div className="lg:col-span-5">
                <div className="grid grid-cols-2 gap-4">
                  {/* Athlete 1 */}
                  <div className="relative rounded-xl overflow-hidden shadow-lg bg-[#0a4075] floating will-change-transform" style={{animationDelay: '0.2s'}}>
                    <Image 
                      src="/images/athletes/athlete1.png"
                      alt="Professional athlete using I-motion" 
                      width={600}
                      height={450}
                      className="w-full h-auto"
                      priority
                    />
                  </div>
                  
                  {/* Athlete 2 */}
                  <div className="relative rounded-xl overflow-hidden shadow-lg bg-[#0a4075] floating will-change-transform" style={{animationDelay: '0.4s'}}>
                    <Image 
                      src="/images/athletes/athlete2.png"
                      alt="Professional athlete using I-motion" 
                      width={600}
                      height={450}
                      className="w-full h-auto"
                    />
                  </div>
                  
                  {/* Athlete 3 */}
                  <div className="relative rounded-xl overflow-hidden shadow-lg bg-[#0a4075] floating will-change-transform" style={{animationDelay: '0.6s'}}>
                    <Image 
                      src="/images/athletes/athlete3.png"
                      alt="Professional athlete using I-motion" 
                      width={600}
                      height={450}
                      className="w-full h-auto"
                    />
                  </div>
                  
                  {/* Athlete 4 */}
                  <div className="relative rounded-xl overflow-hidden shadow-lg bg-[#0a4075] floating will-change-transform" style={{animationDelay: '0.8s'}}>
                    <Image 
                      src="/images/athletes/athlete4.png"
                      alt="Professional athlete using I-motion" 
                      width={600}
                      height={450}
                      className="w-full h-auto"
                    />
                  </div>
                  
                  {/* Athlete 5 */}
                  <div className="relative rounded-xl overflow-hidden shadow-lg bg-[#0a4075] floating will-change-transform" style={{animationDelay: '1s'}}>
                    <Image 
                      src="/images/athletes/athlete5.png"
                      alt="Professional athlete using I-motion" 
                      width={600}
                      height={450}
                      className="w-full h-auto"
                    />
                  </div>
                  
                  {/* Athlete 6 */}
                  <div className="relative rounded-xl overflow-hidden shadow-lg bg-[#0a4075] floating will-change-transform" style={{animationDelay: '1.2s'}}>
                    <Image 
                      src="/images/athletes/athlete6.png"
                      alt="Professional athlete using I-motion" 
                      width={600}
                      height={450}
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* CTA Button */}
            <div className="mt-8 sm:mt-12 text-center">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center bg-imotion text-white font-medium px-6 py-3 rounded-lg hover:bg-imotion-hover transition-all space-x-2 shadow-md group"
              >
                <span>Join Elite Athletes Using I-motion 
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform">
                    <path fillRule="evenodd" d="M2 10a.75.75 0 01.75-.75h12.59l-2.1-1.95a.75.75 0 111.02-1.1l3.5 3.25a.75.75 0 010 1.1l-3.5 3.25a.75.75 0 11-1.02-1.1l2.1-1.95H2.75A.75.75 0 012 10z" clipRule="evenodd" />
                  </svg>
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* Real Users Gallery - Models wearing I-motion product */}
        <section className="py-16 sm:py-20 px-4 bg-gradient-to-br from-slate-50 to-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] font-medium px-4 py-1 rounded-full text-sm mb-4">
                OUR MODELS
              </span>
              <h2 className="text-2xl md:text-4xl font-bold mb-2 sm:mb-4 text-slate-900">
                I-motion in <span className="text-[#38b5a0]">Action</span>
              </h2>
              <p className="text-slate-600 max-w-3xl mx-auto">
                See how our team demonstrates the versatility and effectiveness of I-motion EMS technology
              </p>
            </div>
            
            {/* Horizontal scroll on mobile, grid on larger screens */}
            <div className="relative overflow-hidden">
              <div className="flex overflow-x-auto pb-8 sm:pb-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:overflow-visible snap-x snap-mandatory scrollbar-hide">
                {/* EMS Pulse Model 1 */}
                <div className="min-w-[280px] w-[280px] sm:min-w-0 sm:w-auto snap-center user-card">
                  <div className="bg-white p-2 shadow-md rounded-lg overflow-hidden h-full transform transition-transform hover:translate-y-[-5px]">
                    <div className="relative aspect-[3/2] mb-3 overflow-hidden rounded-md">
                      <Image 
                        src="/images/I-motion-in-action/High-intensity-ems-training.jpg"
                        alt="EMS Pulse model demonstrating strength training with I-motion" 
                        width={300}
                        height={200}
                        className="object-cover w-full h-full hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-0 right-0 bg-[#38b5a0] text-white text-xs px-2 py-1 m-2 rounded">
                        Performance
                      </div>
                    </div>
                    <div className="px-2 pb-1">
                      <h3 className="font-semibold text-slate-900 text-sm">High-intensity training session</h3>
                      <p className="text-xs text-slate-600">Strength & conditioning demonstration</p>
                    </div>
                  </div>
                </div>
                
                {/* EMS Pulse Model 2 */}
                <div className="min-w-[280px] w-[280px] sm:min-w-0 sm:w-auto snap-center user-card">
                  <div className="bg-white p-2 shadow-md rounded-lg overflow-hidden h-full transform transition-transform hover:translate-y-[-5px]">
                    <div className="relative aspect-[3/2] mb-3 overflow-hidden rounded-md">
                      <Image 
                        src="/images/I-motion-in-action/One-on-one-ems-training.jpg"
                        alt="EMS Pulse trainer showcasing I-motion personal training features" 
                        width={300}
                        height={200}
                        className="object-cover w-full h-full hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-0 right-0 bg-[#2389db] text-white text-xs px-2 py-1 m-2 rounded">
                        Personal Training
                      </div>
                    </div>
                    <div className="px-2 pb-1">
                      <h3 className="font-semibold text-slate-900 text-sm">One-on-one instruction</h3>
                      <p className="text-xs text-slate-600">Personalized EMS training approach</p>
                    </div>
                  </div>
                </div>
                
                {/* EMS Pulse Model 3 */}
                <div className="min-w-[280px] w-[280px] sm:min-w-0 sm:w-auto snap-center user-card">
                  <div className="bg-white p-2 shadow-md rounded-lg overflow-hidden h-full transform transition-transform hover:translate-y-[-5px]">
                    <div className="relative aspect-[3/2] mb-3 overflow-hidden rounded-md">
                      <Image 
                        src="/images/I-motion-in-action/ems-suit-group-training.jpg"
                        alt="EMS Pulse team demonstrating group training capabilities" 
                        width={300}
                        height={200}
                        className="object-cover w-full h-full hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-0 right-0 bg-[#38b5a0] text-white text-xs px-2 py-1 m-2 rounded">
                        Group Training
                      </div>
                    </div>
                    <div className="px-2 pb-1">
                      <h3 className="font-semibold text-slate-900 text-sm">Small group configuration</h3>
                      <p className="text-xs text-slate-600">Multi-user EMS setup</p>
                    </div>
                  </div>
                </div>
                
                {/* EMS Pulse Model 4 */}
                <div className="min-w-[280px] w-[280px] sm:min-w-0 sm:w-auto snap-center user-card">
                  <div className="bg-white p-2 shadow-md rounded-lg overflow-hidden h-full transform transition-transform hover:translate-y-[-5px]">
                    <div className="relative aspect-[3/2] mb-3 overflow-hidden rounded-md">
                      <Image 
                        src="/images/I-motion-in-action/ems-suit-rehabilitation-and-recovery.jpg"
                        alt="EMS Pulse specialist demonstrating rehabilitation applications" 
                        width={300}
                        height={200}
                        className="object-cover w-full h-full hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-0 right-0 bg-[#e05c88] text-white text-xs px-2 py-1 m-2 rounded">
                        Rehabilitation
                      </div>
                    </div>
                    <div className="px-2 pb-1">
                      <h3 className="font-semibold text-slate-900 text-sm">Recovery & rehabilitation</h3>
                      <p className="text-xs text-slate-600">Therapeutic muscle activation techniques</p>
                    </div>
                  </div>
                </div>
                
                {/* EMS Pulse Model 5 */}
                <div className="min-w-[280px] w-[280px] sm:min-w-0 sm:w-auto snap-center user-card">
                  <div className="bg-white p-2 shadow-md rounded-lg overflow-hidden h-full transform transition-transform hover:translate-y-[-5px]">
                    <div className="relative aspect-[3/2] mb-3 overflow-hidden rounded-md">
                      <Image 
                        src="/images/I-motion-in-action/ems-suit-facility.jpg"
                        alt="EMS Pulse showcase of studio implementation" 
                        width={300}
                        height={200}
                        className="object-cover w-full h-full hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-0 right-0 bg-[#38b5a0] text-white text-xs px-2 py-1 m-2 rounded">
                        Studio Setup
                      </div>
                    </div>
                    <div className="px-2 pb-1">
                      <h3 className="font-semibold text-slate-900 text-sm">Facility configuration</h3>
                      <p className="text-xs text-slate-600">Professional studio implementation</p>
                    </div>
                  </div>
                </div>
                
                {/* EMS Pulse Model 6 */}
                <div className="min-w-[280px] w-[280px] sm:min-w-0 sm:w-auto snap-center user-card">
                  <div className="bg-white p-2 shadow-md rounded-lg overflow-hidden h-full transform transition-transform hover:translate-y-[-5px]">
                    <div className="relative aspect-[3/2] mb-3 overflow-hidden rounded-md">
                      <Image 
                        src="/images/I-motion-in-action/ems-suit-software.jpg"
                        alt="EMS Pulse team demonstrating performance assessment" 
                        width={300}
                        height={200}
                        className="object-cover w-full h-full hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-0 right-0 bg-[#f8c156] text-white text-xs px-2 py-1 m-2 rounded">
                        Assessment
                      </div>
                    </div>
                    <div className="px-2 pb-1">
                      <h3 className="font-semibold text-slate-900 text-sm">Personalization</h3>
                      <p className="text-xs text-slate-600">Software tailors workouts & tracks progress</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Scroll indicators (visible only on mobile) */}
              <div className="flex justify-center gap-1 mt-4 sm:hidden">
                <span className="h-1 w-10 bg-[#38b5a0] rounded-full"></span>
                <span className="h-1 w-2 bg-slate-300 rounded-full"></span>
                <span className="h-1 w-2 bg-slate-300 rounded-full"></span>
              </div>
            </div>
            
            {/* CTA Button */}
            <div className="mt-8 sm:mt-12 text-center">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center bg-primary text-white font-medium px-8 py-4 rounded-lg hover:bg-primary-hover transition-all shadow-xl shadow-primary/20 hover:translate-y-[-2px] text-lg"
              >
                <span>Request a Consultation</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 ml-2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* Complete Solution Section */}
        <section className="py-16 sm:py-20 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="bg-[#38b5a0]/5 p-6 sm:p-8 md:p-12 rounded-lg border border-[#38b5a0]/10">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4 sm:mb-6">
                <div className="bg-[#38b5a0]/90 rounded-full p-3 text-white self-start sm:self-auto shadow-md">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 sm:w-8 sm:h-8">
                    <path fillRule="evenodd" d="M12.516 2.17a.75.75 0 0 0-1.032 0 11.209 11.209 0 0 1-7.877 3.08.75.75 0 0 0-.722.515A12.74 12.74 0 0 0 2.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 0 0 .374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 0 0-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08Zm3.094 8.016a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                  </svg>
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900">
                  The Complete Wireless EMS Performance Solution
                </h2>
              </div>
              
              <p className="text-base sm:text-lg mb-6 sm:mb-8 text-slate-700">
                The I-motion system includes everything needed for immediate implementation: advanced wireless EMS suits, 
                control unit, software access, and comprehensive training. The intuitive interface allows trainers to easily 
                customize sessions for different performance goals while monitoring activation in real-time.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  href="/contact"
                  className="inline-block bg-imotion text-white font-medium px-5 sm:px-6 py-3 sm:py-4 rounded-lg hover:bg-imotion-hover transition-all shadow-md shadow-imotion/20 text-center text-sm sm:text-base"
                >
                  Request a Quote for the I-motion System
                </Link>
                <Link
                  href="/support-training"
                  className="inline-block border border-slate-200 bg-white text-slate-800 font-medium px-5 sm:px-6 py-3 sm:py-4 rounded-lg hover:bg-slate-50 transition-colors text-center text-sm sm:text-base"
                >
                  Learn About Our Support Package
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

// Replace the VideoContainer component with this simplified version
function ImagePlaceholder() {
  return (
    <div className="relative w-full max-w-[360px] mx-auto">
      <VideoPlayer 
        src="/videos/ems-suit-karim-enzema.mp4" 
        className="rounded-xl overflow-hidden shadow-lg" 
        aspectRatio="portrait"
        preload="metadata"
      />
    </div>
  );
} 